import React from "react";
import styled from "styled-components";

// Window menu bar. Items are { label, items: [{ label, shortcut, onClick,
// disabled, checked }] } for a real dropdown, { label, disabled: true }
// for an honestly-greyed menu, or a plain string — which ALSO renders
// disabled: affordance honesty means nothing looks clickable unless it
// does something. One menu open at a time; clicking elsewhere closes it
// via the transparent backdrop.
function MenuBar({ items, className, ...delegated }) {
  const [openLabel, setOpenLabel] = React.useState(null);
  const wrapperRef = React.useRef(null);

  // Close on any press outside this menubar. A fixed-position backdrop
  // would be clipped by PositionedWindow's transform (fixed scopes to
  // a transformed ancestor); a document listener sidesteps that.
  React.useEffect(() => {
    if (openLabel == null) return undefined;
    const onDocDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpenLabel(null);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [openLabel]);

  const labelOf = (item) => (typeof item === "string" ? item : item.label);

  return (
    <Wrapper role="menubar" className={className} ref={wrapperRef} {...delegated}>
      {items.map((item) => {
        const label = labelOf(item);
        const hasMenu = typeof item !== "string" && Array.isArray(item.items);
        const disabled = !hasMenu || item.disabled === true;
        const open = hasMenu && !disabled && openLabel === label;
        return (
          <ItemWrap key={label}>
            <Item
              type="button"
              role="menuitem"
              disabled={disabled}
              data-open={open ? "true" : undefined}
              data-scene-target={
                typeof item === "string" ? undefined : item.sceneTarget
              }
              onClick={() =>
                hasMenu && setOpenLabel(open ? null : label)
              }
              // Win95 menu mode: once any menu is open, sliding along
              // the bar opens siblings without a click.
              onMouseEnter={() => {
                if (openLabel != null && hasMenu && !disabled) {
                  setOpenLabel(label);
                }
              }}
            >
              <FirstChar>{label[0]}</FirstChar>
              {label.slice(1)}
            </Item>
            {open && (
              <Dropdown className="window">
                {item.items.map((entry) => (
                  <DropdownItem
                    key={entry.label}
                    type="button"
                    role="menuitem"
                    disabled={entry.disabled}
                    data-scene-target={entry.sceneTarget}
                    onClick={() => {
                      setOpenLabel(null);
                      if (entry.onClick) entry.onClick();
                    }}
                  >
                    <Check aria-hidden="true">
                      {entry.checked ? "•" : ""}
                    </Check>
                    <Label>{entry.label}</Label>
                    {entry.shortcut && <Shortcut>{entry.shortcut}</Shortcut>}
                  </DropdownItem>
                ))}
              </Dropdown>
            )}
          </ItemWrap>
        );
      })}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  margin: 1px 0;
  width: 100%;
`;

const ItemWrap = styled.div`
  position: relative;
`;

const Item = styled.button`
  background: transparent;
  border: none;
  box-shadow: none;
  min-height: 0;
  min-width: 0;
  padding: 2px 5px;
  color: inherit;
  text-shadow: none;
  font: inherit;
  cursor: default;

  &&:not(:disabled):not([data-open="true"]):hover,
  &&[data-ghost-hover="true"]:not([data-open="true"]),
  &&[data-open="true"] {
    box-shadow:
      inset 1px 1px #fff,
      inset -1px -1px #808080;
  }

  &&:not(:disabled):active,
  &&[data-open="true"],
  &&[data-open="true"]:hover {
    box-shadow:
      inset 1px 1px #808080,
      inset -1px -1px #fff;
    text-shadow: none;
  }

  &&:focus {
    outline: none;
  }
  &&:focus-visible {
    box-shadow:
      inset 1px 1px #fff,
      inset -1px -1px #808080;
  }

  &&:disabled {
    color: #808080;
    text-shadow: 1px 1px 0 #fff;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% - 1px); /* Nolen 2026-09-21: reads 1px high in the ref shots */
  left: 0;
  z-index: 950;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  padding: 2px;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  box-shadow: none;
  min-height: 0;
  min-width: 0;
  padding: 4px 10px;
  text-align: left;
  font: inherit;
  color: inherit;
  text-shadow: none;
  white-space: nowrap;

  &&:not(:disabled):hover,
  &&[data-ghost-hover="true"] {
    background: #00007b;
    color: #fff;
    box-shadow: none;
  }
  &&:not(:disabled):active {
    box-shadow: none;
    text-shadow: none;
  }
  &&:focus {
    outline: none;
  }
  &&:focus-visible {
    background: #00007b;
    color: #fff;
  }
  &&:disabled {
    color: #808080;
  }
`;

const Shortcut = styled.span`
  font-size: 12px;
  opacity: 0.75;
`;

// Win95 View menus mark the current choice with a leading dot.
const Check = styled.span`
  width: 10px;
  flex-shrink: 0;
  text-align: center;
`;

const Label = styled.span`
  flex: 1 1 auto;
`;

const FirstChar = styled.span`
  text-decoration: underline;
  text-decoration-thickness: 1px; /* integer px — pixel-font rule */
`;

export default MenuBar;
